import React, { useState, useEffect, useMemo } from 'react';
import { Search, MapPin, Calendar, Eye, RefreshCw, Users, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';

interface POINotification {
    id: number;
    user_id: number;
    user_username: string;
    user_avatar?: string;
    source_user_id?: number;
    source_username?: string;
    source_name?: string;
    source_avatar?: string;
    title: string;
    message: string;
    is_read: boolean;
    created_at: string;
}

interface POIStats {
    total: number;
    today: number;
    unread: number;
    this_week: number;
}

const CATEGORY_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
    'cafe':       { label: 'Kafe',         color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
    'restaurant': { label: 'Restoran',     color: 'text-red-700',    bg: 'bg-red-50',     border: 'border-red-200' },
    'fast_food':  { label: 'Fast Food',    color: 'text-yellow-700', bg: 'bg-yellow-50',  border: 'border-yellow-200' },
    'pizza':      { label: 'Pizza',        color: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200' },
    'bar':        { label: 'Bar/Pub',      color: 'text-purple-700', bg: 'bg-purple-50',  border: 'border-purple-200' },
    'pub':        { label: 'Bar/Pub',      color: 'text-purple-700', bg: 'bg-purple-50',  border: 'border-purple-200' },
    'supermarket':{ label: 'Market',       color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200' },
    'mall':       { label: 'AVM',          color: 'text-pink-700',   bg: 'bg-pink-50',    border: 'border-pink-200' },
    'gym':        { label: 'Spor Salonu',  color: 'text-indigo-700', bg: 'bg-indigo-50',  border: 'border-indigo-200' },
    'hospital':   { label: 'Hastane',      color: 'text-rose-700',   bg: 'bg-rose-50',    border: 'border-rose-200' },
    'clinic':     { label: 'Klinik',       color: 'text-rose-700',   bg: 'bg-rose-50',    border: 'border-rose-200' },
    'pharmacy':   { label: 'Eczane',       color: 'text-teal-700',   bg: 'bg-teal-50',    border: 'border-teal-200' },
    'dentist':    { label: 'Diş Hekimi',   color: 'text-teal-700',   bg: 'bg-teal-50',    border: 'border-teal-200' },
    'bank':       { label: 'Banka',        color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200' },
    'fuel':       { label: 'Benzinlik',    color: 'text-slate-700',  bg: 'bg-slate-50',   border: 'border-slate-200' },
    'park':       { label: 'Park',         color: 'text-emerald-700',bg: 'bg-emerald-50', border: 'border-emerald-200' },
    'beach':      { label: 'Plaj',         color: 'text-cyan-700',   bg: 'bg-cyan-50',    border: 'border-cyan-200' },
    'cinema':     { label: 'Sinema',       color: 'text-violet-700', bg: 'bg-violet-50',  border: 'border-violet-200' },
    'theatre':    { label: 'Tiyatro',      color: 'text-violet-700', bg: 'bg-violet-50',  border: 'border-violet-200' },
    'museum':     { label: 'Müze',         color: 'text-stone-700',  bg: 'bg-stone-50',   border: 'border-stone-200' },
    'library':    { label: 'Kütüphane',    color: 'text-stone-700',  bg: 'bg-stone-50',   border: 'border-stone-200' },
    'airport':    { label: 'Havalimanı',   color: 'text-sky-700',    bg: 'bg-sky-50',     border: 'border-sky-200' },
    'hotel':      { label: 'Otel',         color: 'text-fuchsia-700',bg: 'bg-fuchsia-50', border: 'border-fuchsia-200' },
    'school':     { label: 'Okul',         color: 'text-lime-700',   bg: 'bg-lime-50',    border: 'border-lime-200' },
    'university': { label: 'Üniversite',   color: 'text-lime-700',   bg: 'bg-lime-50',    border: 'border-lime-200' },
    'mosque':     { label: 'Cami',         color: 'text-teal-700',   bg: 'bg-teal-50',    border: 'border-teal-200' },
    'church':     { label: 'Kilise',       color: 'text-teal-700',   bg: 'bg-teal-50',    border: 'border-teal-200' },
    'stadium':    { label: 'Stadyum',      color: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200' },
    'zoo':        { label: 'Hayvanat B.',  color: 'text-amber-700',  bg: 'bg-amber-50',   border: 'border-amber-200' },
    'post_office':{ label: 'Postane',      color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200' },
    'train_station': { label: 'Tren Garı',  color: 'text-slate-700',  bg: 'bg-slate-50',   border: 'border-slate-200' },
    'bus_station':{ label: 'Otobüs Term.', color: 'text-slate-700',  bg: 'bg-slate-50',   border: 'border-slate-200' },
};

const EMOJI_TO_CATEGORY: Record<string, string> = {
    '☕': 'cafe', '🍽️': 'restaurant', '🍔': 'fast_food', '🍕': 'pizza',
    '🍺': 'bar', '🛒': 'supermarket', '🛍️': 'mall', '💪': 'gym',
    '🏥': 'hospital', '💊': 'pharmacy', '🦷': 'dentist', '🏦': 'bank',
    '⛽': 'fuel', '🌳': 'park', '🏖️': 'beach', '🎬': 'cinema',
    '🎭': 'theatre', '🏛️': 'museum', '📚': 'library', '✈️': 'airport',
    '🏨': 'hotel', '🏫': 'school', '🎓': 'university', '🕌': 'mosque',
    '⛪': 'church', '🏟️': 'stadium', '🦁': 'zoo', '📮': 'post_office',
    '🚂': 'train_station', '🚌': 'bus_station',
};

const POIManagement: React.FC = () => {
    const [notifications, setNotifications] = useState<POINotification[]>([]);
    const [stats, setStats] = useState<POIStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [notifsRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/poi-notifications'),
                axios.get('https://api.lifeshare.app/api/v1/admin/poi-notifications/stats')
            ]);
            setNotifications(notifsRes.data.notifications || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const getEmojiFromMessage = (message: string) => {
        for (const ch of message) {
            if (EMOJI_TO_CATEGORY[ch]) return ch;
        }
        return '📍';
    };

    const getCategoryFromMessage = (message: string) => {
        for (const ch of message) {
            const cat = EMOJI_TO_CATEGORY[ch];
            if (cat) return cat;
        }
        return 'other';
    };

    const getCategoryMeta = (category: string) => {
        return CATEGORY_META[category] || { label: 'Diğer', color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' };
    };

    const formatRelativeTime = (dateStr: string) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);
        if (diffMin < 1) return 'Az önce';
        if (diffMin < 60) return `${diffMin} dk önce`;
        const diffHr = Math.floor(diffMin / 60);
        if (diffHr < 24) return `${diffHr} saat önce`;
        const diffDay = Math.floor(diffHr / 24);
        if (diffDay === 1) return 'Dün';
        if (diffDay < 7) return `${diffDay} gün önce`;
        return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    };

    const toggleGroup = (category: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(category)) next.delete(category);
            else next.add(category);
            return next;
        });
    };

    const filteredNotifications = notifications.filter(n => {
        const matchesSearch =
            (n.source_username || '').toLowerCase().includes(searchText.toLowerCase()) ||
            (n.source_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
            (n.message || '').toLowerCase().includes(searchText.toLowerCase());

        let matchesDate = true;
        if (dateFrom) {
            matchesDate = new Date(n.created_at) >= new Date(dateFrom + 'T00:00:00');
        }
        if (dateTo && matchesDate) {
            matchesDate = new Date(n.created_at) <= new Date(dateTo + 'T23:59:59');
        }

        return matchesSearch && matchesDate;
    });

    const groupedNotifications = useMemo(() => {
        const groups: Record<string, POINotification[]> = {};
        for (const n of filteredNotifications) {
            const cat = getCategoryFromMessage(n.message);
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(n);
        }
        const sortedKeys = Object.keys(groups).sort((a, b) => {
            const aTime = new Date(groups[a][0].created_at).getTime();
            const bTime = new Date(groups[b][0].created_at).getTime();
            return bTime - aTime;
        });
        return { groups, sortedKeys };
    }, [filteredNotifications]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Popüler Yerler (POI)</h1>
                        <p className="text-sm text-gray-500 mt-1">Kafe, restoran, AVM vb. bildirimler</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><MapPin className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
                            <p className="text-xs text-purple-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Calendar className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.today}</p>
                            <p className="text-xs text-green-600">Bugün</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Eye className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-blue-700">{stats.unread}</p>
                            <p className="text-xs text-blue-600">Okunmamış</p>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Calendar className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-orange-700">{stats.this_week}</p>
                            <p className="text-xs text-orange-600">Bu Hafta</p>
                        </div>
                    </div>
                )}

                {/* Category Tags */}
                <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">☕ Kafe</span>
                    <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">🍽️ Restoran</span>
                    <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">🍔 Fast Food</span>
                    <span className="px-3 py-1.5 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">🛍️ AVM</span>
                    <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">🌳 Park</span>
                    <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">💪 Spor</span>
                </div>

                {/* Search + Date Filter */}
                <div className="bg-white rounded-2xl p-4 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı veya mekan ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 items-end">
                        <div className="flex-1 w-full">
                            <label className="text-xs text-gray-500 mb-1 block">Başlangıç</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <div className="flex-1 w-full">
                            <label className="text-xs text-gray-500 mb-1 block">Bitiş</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <button
                            onClick={() => { setDateFrom(''); setDateTo(''); }}
                            className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
                        >
                            Temizle
                        </button>
                    </div>
                </div>

                {/* List — Grouped by Category */}
                {filteredNotifications.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">POI bildirimi bulunamadı</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {groupedNotifications.sortedKeys.map((category) => {
                            const items = groupedNotifications.groups[category];
                            const meta = getCategoryMeta(category);
                            const emoji = getEmojiFromMessage(items[0].message);
                            const isExpanded = expandedGroups.has(category);
                            const uniqueUsers = new Set(items.map(i => i.source_user_id)).size;
                            const latestTime = items[0].created_at;

                            return (
                                <div key={category} className={`bg-white rounded-2xl border ${meta.border} overflow-hidden`}>
                                    {/* Group Header */}
                                    <button
                                        onClick={() => toggleGroup(category)}
                                        className={`w-full flex items-center gap-3 p-4 ${meta.bg} hover:brightness-95 transition-all`}
                                    >
                                        <span className="text-2xl flex-shrink-0">{emoji}</span>
                                        <div className="flex-1 text-left">
                                            <div className="flex items-center gap-2">
                                                <span className={`font-semibold ${meta.color}`}>{meta.label}</span>
                                                <span className={`px-2 py-0.5 ${meta.bg} ${meta.color} rounded-full text-xs font-medium border ${meta.border}`}>
                                                    {items.length} bildirim
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Users className="w-3 h-3" />
                                                    {uniqueUsers} kullanıcı
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    En son: {formatRelativeTime(latestTime)}
                                                </span>
                                            </div>
                                        </div>
                                        {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                                    </button>

                                    {/* Group Items */}
                                    {isExpanded && (
                                        <div className="divide-y divide-gray-50">
                                            {items.map((notif) => (
                                                <div key={notif.id} className="p-4 hover:bg-gray-50/50 transition-colors">
                                                    <div className="flex items-start gap-3">
                                                        {notif.source_avatar ? (
                                                            <img src={`https://api.lifeshare.app${notif.source_avatar}`} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
                                                        ) : (
                                                            <div className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center text-lg flex-shrink-0`}>
                                                                {emoji}
                                                            </div>
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-semibold text-gray-900 text-sm truncate">
                                                                    {notif.source_name || notif.source_username || 'Bilinmeyen'}
                                                                </p>
                                                                {!notif.is_read && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                                                            </div>
                                                            <p className="text-xs text-gray-500">@{notif.source_username || 'unknown'}</p>
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            <p className="text-xs text-gray-400">
                                                                {new Date(notif.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                            </p>
                                                            <p className="text-xs text-gray-400 mt-0.5">
                                                                {formatRelativeTime(notif.created_at)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="mt-2.5 ml-12 p-2.5 bg-gray-50 rounded-lg">
                                                        <p className="text-sm text-gray-700">{notif.message}</p>
                                                    </div>

                                                    {notif.user_username && notif.user_username !== notif.source_username && (
                                                        <div className="mt-1.5 ml-12 text-xs text-gray-400">
                                                            Bildirim alan: <span className="font-medium text-gray-600">@{notif.user_username}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default POIManagement;
